import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Crown, Check, AlertCircle, Tag, History, Calendar, Lock, Star, Rocket, Award, Zap, Trophy, Medal, Gem, Diamond, Sparkles, Flame, Shield, Target, TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp, Eye, Users, UserCheck, CheckCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

// Icon mapping for dynamic rendering
const IconMap = {
  Crown, Star, Rocket, Award, Zap, Trophy, Medal,
  Gem, Diamond, Sparkles, Fire: Flame, Bolt: Zap, Shield, Target,
  TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp,
  Eye, Users, UserCheck, Verified: BadgeCheck, CheckCircle
};

const renderIcon = (iconName, className = "w-5 h-5") => {
  const IconComponent = IconMap[iconName];
  if (IconComponent) {
    return <IconComponent className={className} />;
  }
  return <Star className={className} />;
};

const MembershipTab = ({ onNavigateToListingCredits }) => {
  const navigate = useNavigate();
  const [memberships, setMemberships] = useState([]);
  const [boostCategories, setBoostCategories] = useState([]);
  const [activeMembership, setActiveMembership] = useState(null);
  const [boostHistory, setBoostHistory] = useState([]);
  const [hasActiveListingCredit, setHasActiveListingCredit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDowngradeWarning, setShowDowngradeWarning] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [finalPrice, setFinalPrice] = useState(0);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchMemberships();
    fetchBoostCategories();
    fetchActiveMembership();
    fetchBoostHistory();
    fetchListingCreditStatus();
  }, []);

  const fetchBoostCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/boost-categories`);
      if (response.ok) {
        const data = await response.json();
        setBoostCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching boost categories:', error);
    }
  };

  const fetchMemberships = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/memberships`);
      if (response.ok) {
        const data = await response.json();
        setMemberships(data.memberships || []);
      }
    } catch (error) {
      console.error('Error fetching memberships:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchListingCreditStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/listing-credits/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHasActiveListingCredit(data.has_active_credit || false);
      }
    } catch (error) {
      console.error('Error fetching listing credit status:', error);
    }
  };

  const fetchActiveMembership = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/membership`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setActiveMembership(data.membership);
      }
    } catch (error) {
      console.error('Error fetching active membership:', error);
    }
  };

  const fetchBoostHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/trainers/membership-history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBoostHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching boost history:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSelectPlan = (membership) => {
    setSelectedPlan(membership);
    setFinalPrice(membership.price);
    setCouponCode('');
    setCouponApplied(null);
    
    // Check if this is a downgrade from Boosted to Regular
    if (activeMembership?.membership_type === 'boosted' && (membership.type === 'listing' || membership.type === 'regular')) {
      setShowDowngradeWarning(true);
    } else {
      setShowModal(true);
    }
  };

  const handleDowngradeConfirm = () => {
    setShowDowngradeWarning(false);
    setShowModal(true);
  };

  const handleDowngradeCancel = () => {
    setShowDowngradeWarning(false);
    setSelectedPlan(null);
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast({ title: 'Error', description: 'Please enter a coupon code', variant: 'destructive' });
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/coupons/validate?code=${couponCode}&amount=${selectedPlan.price}&membership_type=${selectedPlan.type}&coupon_type=membership`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setCouponApplied(data.coupon);
        const discountedPrice = selectedPlan.price - data.coupon.discount_amount;
        setFinalPrice(discountedPrice);
        toast({
          title: 'Success!',
          description: `Coupon applied! You save $${data.coupon.discount_amount.toFixed(2)}`,
        });
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Invalid coupon code', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to apply coupon', variant: 'destructive' });
    }
  };

  const handleConfirmPurchase = async () => {
    if (!selectedPlan) return;

    // Navigate to payment page with package details for PayPal
    navigate('/payment', {
      state: {
        package: {
          ...selectedPlan,
          package_id: selectedPlan.membership_id || selectedPlan._id || selectedPlan.id,
          type: 'boosted',
          currency: selectedPlan.currency || 'CAD',
          couponCode: couponApplied ? couponCode : null,
          couponApplied: couponApplied,
          price: finalPrice
        }
      }
    });
    
    // Close modal
    setShowModal(false);
  };

  // Note: regularMemberships no longer includes 'listing' type as it's been removed
  const regularMemberships = memberships.filter(m => m.type === 'regular');
  const boostedMemberships = memberships.filter(m => m.type === 'boosted');
  
  // Group memberships by category dynamically
  const getMembershipsByCategory = () => {
    const grouped = {};
    
    // First, add all categories from boostCategories (excluding 'listing' which is not a meaningful category)
    boostCategories
      .filter(category => category.key !== 'listing')
      .forEach(category => {
        grouped[category.key] = {
          category: category,
          memberships: []
        };
      });
    
    // Then, add memberships to their respective categories (skip 'listing' type memberships)
    memberships
      .filter(membership => membership.type !== 'listing')
      .forEach(membership => {
        const type = membership.type;
        if (grouped[type]) {
          grouped[type].memberships.push(membership);
        } else {
          // If category not found, create a fallback
          grouped[type] = {
            category: {
              key: type,
              label: type.charAt(0).toUpperCase() + type.slice(1),
              icon: 'Star',
              color: '#6B7280'
            },
            memberships: [membership]
          };
        }
      });
    
    // Filter out categories with no memberships and sort by sort_order
    return Object.values(grouped)
      .filter(group => group.memberships.length > 0)
      .sort((a, b) => (a.category.sort_order || 0) - (b.category.sort_order || 0));
  };

  const groupedMemberships = getMembershipsByCategory();
  
  // Get category info for a membership type
  const getCategoryInfo = (type) => {
    return boostCategories.find(c => c.key === type) || {
      label: type === 'boosted' ? 'Boosted Premium' : type,
      icon: type === 'boosted' ? 'Crown' : 'Star',
      color: type === 'boosted' ? '#EAB308' : '#3B82F6'
    };
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Active Membership Banner */}
      {activeMembership && (
        <div className="rounded-lg border bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-700">
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300">Active Boost Package</h3>
                <p className="text-blue-700 dark:text-blue-400 mt-1 flex items-center gap-2">
                  {(() => {
                    const categoryInfo = getCategoryInfo(activeMembership.membership_type);
                    return (
                      <>
                        {renderIcon(categoryInfo.icon || 'Star', 'w-4 h-4')}
                        <span style={{ color: categoryInfo.color }}>{categoryInfo.label}</span>
                      </>
                    );
                  })()}
                </p>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-2">
                  Valid until: {new Date(activeMembership.end_date).toLocaleDateString()}
                </p>
              </div>
              <Badge className="bg-green-500 text-white px-4 py-2">
                <Check className="w-4 h-4 mr-1" />
                Active
              </Badge>
            </div>
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="rounded-lg border bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700">
        <div className="p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div>
              <h4 className="font-semibold text-amber-900 dark:text-amber-300">About Boost Packages</h4>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                Boost packages increase your visibility and help you reach more clients. Regular listings appear in search results, 
                while Boosted listings get priority placement and featured badges.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* No Active Listing Credit Warning */}
      {!hasActiveListingCredit && (
        <div className="rounded-lg border-2 bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <Lock className="w-6 h-6 text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-900 dark:text-red-300 text-lg">Listing Credit Required</h4>
                <p className="text-sm text-red-700 dark:text-red-400 mt-2">
                  You need an <strong>active Listing Credit</strong> before you can purchase a Boost Package. 
                  Listing Credits make your profile visible on the homepage.
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                  Please purchase a Listing Credit first from the <strong>&quot;Listing Credits&quot;</strong> tab.
                </p>
                <Button 
                  onClick={() => {
                    if (onNavigateToListingCredits) {
                      onNavigateToListingCredits();
                    } else {
                      navigate('/trainer/dashboard', { state: { tab: 'listing-credits' } });
                    }
                  }}
                  className="mt-4 bg-red-600 hover:bg-red-700 text-white"
                >
                  Go to Listing Credits →
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Category Sections */}
      {groupedMemberships.map((group) => {
        const category = group.category;
        const categoryMemberships = group.memberships;
        const isDefaultCategory = category.key === 'regular' || category.key === 'boosted';
        const isPremiumStyle = category.key === 'boosted' || (!isDefaultCategory && category.color !== '#3B82F6');
        
        return (
          <div key={category.key} className={!hasActiveListingCredit ? 'opacity-50 pointer-events-none' : ''}>
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
              {renderIcon(category.icon || 'Star', 'w-5 h-5')}
              <span style={{ color: category.color }}>{category.label}</span>
              {category.description && (
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                  - {category.description}
                </span>
              )}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categoryMemberships.map((membership) => (
                <div 
                  key={membership.membership_id} 
                  className={`hover:shadow-lg transition-shadow rounded-lg border-2 ${
                    isPremiumStyle 
                      ? 'bg-gradient-to-br from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20' 
                      : 'bg-white dark:bg-gray-800'
                  }`}
                  style={{ borderColor: category.color || '#e5e7eb' }}
                >
                  <div 
                    className="p-4 border-b"
                    style={{ borderColor: `${category.color}40` || '#e5e7eb' }}
                  >
                    <h4 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
                      {membership.name}
                      {isPremiumStyle && renderIcon(category.icon || 'Crown', 'w-4 h-4')}
                    </h4>
                  </div>
                  <div className="p-4 space-y-4">
                    <div>
                      <p 
                        className="text-3xl font-bold"
                        style={{ color: category.color || '#3B82F6' }}
                      >
                        ${membership.price} <span className="text-sm text-gray-600 dark:text-gray-400">{membership.currency}</span>
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{membership.duration_days} days</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold mb-2 text-gray-900 dark:text-white">Features:</p>
                      <ul className="space-y-1">
                        {(membership.features || []).map((feature, idx) => (
                          <li key={idx} className="text-sm flex items-start gap-2 text-gray-700 dark:text-gray-300">
                            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className={isPremiumStyle ? 'font-medium' : ''}>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button 
                      onClick={() => handleSelectPlan(membership)}
                      className="w-full"
                      style={isPremiumStyle ? { 
                        background: `linear-gradient(to right, ${category.color}, ${category.color}dd)` 
                      } : {}}
                      disabled={activeMembership?.membership_type === membership.type || !hasActiveListingCredit}
                    >
                      {activeMembership?.membership_type === membership.type ? 'Current Plan' : 'Select Plan'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Boost Package History */}
      {boostHistory.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <History className="w-5 h-5" />
            Boost Package History
          </h3>
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="p-6">
              <div className="space-y-3">
                {boostHistory.map((item, index) => (
                  <div 
                    key={item.purchase_id || index}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      item.package_type === 'boosted' 
                        ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-700' 
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {item.package_type === 'boosted' ? (
                          <Crown className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                        ) : (
                          <Check className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        )}
                        <p className="font-semibold text-gray-900 dark:text-white">{item.package_name}</p>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(item.start_date)} - {formatDate(item.end_date)}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Duration: <span className="font-medium text-gray-900 dark:text-white">{item.duration_days} days</span>
                        </p>
                        {item.coupon_code && (
                          <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            Coupon: {item.coupon_code}
                          </p>
                        )}
                      </div>
                      <p className="text-sm mt-1">
                        {item.discount_amount > 0 ? (
                          <>
                            <span className="text-gray-400 line-through">${item.original_price?.toFixed(2)}</span>
                            <span className="ml-2 font-semibold text-green-600 dark:text-green-400">Paid: ${item.amount_paid?.toFixed(2)}</span>
                            <span className="ml-2 text-xs text-green-500 dark:text-green-400">(Saved ${item.discount_amount?.toFixed(2)})</span>
                          </>
                        ) : (
                          <span className="font-semibold text-gray-900 dark:text-white">Paid: ${item.amount_paid?.toFixed(2)}</span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge 
                        variant={item.status === 'active' ? 'default' : 'secondary'}
                        className={item.status === 'active' 
                          ? item.package_type === 'boosted' 
                            ? 'bg-yellow-500' 
                            : 'bg-blue-500'
                          : ''
                        }
                      >
                        {item.status === 'active' ? 'Active' : 'Expired'}
                      </Badge>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatDate(item.purchased_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Downgrade Warning Modal */}
      {showDowngradeWarning && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-2">
                <AlertCircle className="w-6 h-6" />
                Boost Package Downgrade Warning
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4">
                <p className="text-gray-800 dark:text-gray-200 mb-3">
                  <strong>Attention!</strong> You currently have a <span className="text-orange-600 dark:text-orange-400 font-semibold">Boosted (Premium)</span> boost package.
                </p>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  The selected <span className="font-semibold">{selectedPlan.name}</span> plan is a <strong>lower tier</strong> than your current boost package.
                </p>
                <p className="text-gray-800 dark:text-gray-200 font-medium">
                  Do you still want to confirm this payment?
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-300">
                  <strong>Note:</strong> If you confirm this action, your current Boosted boost package will end and you will switch to a Regular Listing boost package.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleDowngradeCancel}
                  variant="outline"
                  className="flex-1 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  No, Cancel
                </Button>
                <Button
                  onClick={handleDowngradeConfirm}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Yes, Continue
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {showModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                {selectedPlan.type === 'boosted' && <Crown className="w-5 h-5 text-yellow-500" />}
                Confirm Purchase
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg space-y-2 border border-gray-200 dark:border-gray-600">
                <p className="font-semibold text-gray-900 dark:text-white">{selectedPlan.name}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{selectedPlan.duration_days} days</p>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700 dark:text-gray-300">Original Price:</span>
                    <span className={couponApplied ? 'line-through text-gray-400' : 'font-semibold text-gray-900 dark:text-white'}>
                      ${selectedPlan.price.toFixed(2)} {selectedPlan.currency}
                    </span>
                  </div>
                  {couponApplied && (
                    <>
                      <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                        <span>Discount ({couponApplied.discount_type === 'percentage' ? `${couponApplied.discount_value}%` : `$${couponApplied.discount_value}`}):</span>
                        <span>- ${couponApplied.discount_amount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                        <span className="text-gray-900 dark:text-white">Final Price:</span>
                        <span className="text-green-600 dark:text-green-400">${finalPrice.toFixed(2)} {selectedPlan.currency}</span>
                      </div>
                    </>
                  )}
                  {!couponApplied && (
                    <div className="flex justify-between text-lg font-bold mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                      <span className="text-gray-900 dark:text-white">Total:</span>
                      <span className="text-gray-900 dark:text-white">${finalPrice.toFixed(2)} {selectedPlan.currency}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Coupon Code Section */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                  <Tag className="w-4 h-4" />
                  Have a coupon code?
                </Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    disabled={couponApplied !== null}
                  />
                  <Button
                    onClick={handleApplyCoupon}
                    variant="outline"
                    disabled={couponApplied !== null || !couponCode.trim()}
                    className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    {couponApplied ? 'Applied' : 'Apply'}
                  </Button>
                </div>
                {couponApplied && (
                  <p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
                    <Check className="w-4 h-4" />
                    Coupon applied successfully!
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedPlan(null);
                    setCouponCode('');
                    setCouponApplied(null);
                  }}
                  variant="outline"
                  className="flex-1 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  disabled={purchasing}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmPurchase}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white"
                  disabled={purchasing}
                >
                  Proceed to Payment →
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipTab;
